import React, { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'src/store/Store';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';

// Type declaration for DOM globals
declare const document: any;
//? Example for SVG :
// @ts-ignore
import { ReactComponent as LogoLightSm } from 'src/assets/images/logos/logo-sm.svg';
// @ts-ignore
import { ReactComponent as LogoDarkSm } from 'src/assets/images/logos/logo-sm-white.svg';

import logoDark from 'src/assets/images/logos/secuseek-logo-white.svg';
import logoLight from 'src/assets/images/logos/secuseek-logo.svg';

import { Box, Button, Chip, Dialog, IconButton, Stack, styled, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';
import { AppState } from 'src/store/Store';
import { getLatestVersion, getLatestVersionPreRelease, getLatestVersionPreReleaseVersion, getLatestVersionSimple, getVersionTypeColor } from 'src/utils/utils';
import Scrollbar from 'src/components/custom-scroll/Scrollbar';
import { changelog } from '../../../../version-log';
import { IconX } from '@tabler/icons-react';

interface LogoProps {
  align: 'start' | 'center' | 'end';
}

// Global state for dialog management - completely isolated from Redux
let globalDialogState = {
  isOpen: false,
  listeners: new Set<() => void>(),
};

const setGlobalDialogState = (isOpen: boolean) => {
  globalDialogState.isOpen = isOpen;
  globalDialogState.listeners.forEach((listener) => listener());
};

// Hook to subscribe to global dialog state
const useGlobalDialogState = () => {
  const [isOpen, setIsOpen] = useState(globalDialogState.isOpen);

  useEffect(() => {
    const listener = () => setIsOpen(globalDialogState.isOpen);
    globalDialogState.listeners.add(listener);
    return () => {
      globalDialogState.listeners.delete(listener);
    };
  }, []);

  return [isOpen, setGlobalDialogState] as const;
};

// Memoized version data to prevent recalculation
const useVersionData = () =>
  useMemo(() => {
    const latestVersion = getLatestVersion(changelog);
    const preReleaseType = getLatestVersionPreRelease(changelog);
    const preReleaseVersion = getLatestVersionPreReleaseVersion(changelog);
    const latestVersionSimple = getLatestVersionSimple(changelog);

    return { latestVersion, preReleaseType, preReleaseVersion, latestVersionSimple };
  }, []);

// Memoized changelog data
const useChangelogData = () =>
  useMemo(() => {
    return changelog
      ?.filter((item: any) => item.hidden !== true)
      ?.sort(sortDescVersionsByVersionName)
      ?.sort(sortDescVersionsByDate);
  }, []);

const sortDescVersionsByDate = (a: any, b: any) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime();
const sortDescVersionsByVersionName = (a: any, b: any) => b.version.replace(/\D/g, '') - a.version.replace(/\D/g, '');

// Completely isolated dialog component that doesn't depend on Redux state
const VersionHistoryDialog = memo(() => {
  const [isOpen, setIsOpen] = useGlobalDialogState();
  const [visibleCount, setVisibleCount] = useState(8);
  const dialogRef = useRef<any>(null);
  const visibleSortedChangelog = useChangelogData();

  // Get the limited versions to display
  const displayedVersions = useMemo(() => {
    return visibleSortedChangelog?.slice(0, visibleCount) || [];
  }, [visibleSortedChangelog, visibleCount]);

  // Check if there are more versions to load
  const hasMoreVersions = useMemo(() => {
    return visibleSortedChangelog && visibleSortedChangelog.length > visibleCount;
  }, [visibleSortedChangelog, visibleCount]);

  // Handle load more functionality
  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + 8);
  }, []);

  // Reset visible count when dialog opens
  useEffect(() => {
    if (isOpen) {
      setVisibleCount(8);
    }
  }, [isOpen]);

  // Memoize the dialog content to prevent rerenders
  const dialogContent = useMemo(
    () => (
      <Box ref={dialogRef}>
        <Box onClick={() => setIsOpen(false)} sx={{ position: 'absolute', top: 0, right: 0, cursor: 'pointer', zIndex: 999 }}>
          <IconButton size="small" sx={{ '&:hover': { opacity: 1 }, opacity: 0.5 }} aria-label={'IconX'}>
            <IconX size="24" />
          </IconButton>
        </Box>

        <Typography id="header-diagram" variant="h4" sx={{ py: 2, px: { xs: 2, md: 3 } }}>
          Version History
        </Typography>
        <Scrollbar
          sx={{
            height: 'fit-content',
            maxHeight: '68vh',
            '& .MuiGrid-root': { paddingY: 0 },
            mb: 4,
          }}
        >
          <Box sx={{ py: 2, px: { xs: 2, md: 3 }, ml: 1 }}>
            <Stack spacing={2}>
              {displayedVersions?.map((release, index) => (
                <Box key={release.version}>
                  <Typography variant="h6" color="info.dark">
                    Version {release.version}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Released on: {new Date(release.release_date).toLocaleDateString()}
                  </Typography>
                  <Stack spacing={1} sx={{ pl: { xs: 0.5, md: 4 } }}>
                    {release.changes?.map(
                      (change, idx) =>
                        change.hidden !== true && (
                          <Stack key={idx} direction="row" justifyContent="space-between" alignItems="start" spacing={1}>
                            <Box>
                              <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="text.primary" fontWeight={600}>
                                  • {change.title}
                                  {change.issue_number && ` (#${change.issue_number})`}{' '}
                                </Typography>
                                {change.type && (
                                  <Chip
                                    label={change.type?.slice(0, 7)}
                                    sx={{
                                      display: { xs: 'block', md: 'none' },
                                      height: 'min-content',
                                      '& .MuiChip-label': { fontSize: '9px', letterSpacing: '1px', fontWeight: 500, lineHeight: '14px' },
                                      width: 'fit-content',
                                      scale: 0.9,
                                    }}
                                    size="small"
                                    color={getVersionTypeColor(change.type)}
                                  />
                                )}
                              </Stack>
                              {Array.isArray(change.description) ? (
                                <Stack spacing={0.5}>
                                  {change.description.map((desc, descIdx) => (
                                    <Typography key={descIdx} variant="body2" color="text.secondary">
                                      - {desc}
                                    </Typography>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  - {change.description}
                                </Typography>
                              )}
                            </Box>

                            {change.type && (
                              <Chip
                                label={change.type}
                                sx={{
                                  display: { xs: 'none', md: 'block' },
                                  height: '19px',
                                  '& .MuiChip-label': { fontSize: '9px', letterSpacing: '1px', fontWeight: 500, lineHeight: '10px' },
                                }}
                                size="small"
                                color={getVersionTypeColor(change.type)}
                              />
                            )}
                          </Stack>
                        ),
                    )}
                  </Stack>
                  {index < displayedVersions?.length - 1 && <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: 2, mb: 2 }} />}
                </Box>
              ))}
            </Stack>

            {/* Load More Button */}
            {hasMoreVersions && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Button
                  variant="text"
                  onClick={handleLoadMore}
                  color="info"
                  sx={{
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'info.dark',
                      textDecoration: 'underline',
                    },
                    textTransform: 'none',
                    px: 3,
                    py: 0.5,
                  }}
                >
                  Load more
                </Button>
              </Box>
            )}
          </Box>
        </Scrollbar>
      </Box>
    ),
    [displayedVersions, hasMoreVersions, handleLoadMore, setIsOpen],
  );

  if (!isOpen) return null;

  return createPortal(
    <Dialog
      open={isOpen}
      onClose={(event: any, reason?: string) => {
        if (reason === 'backdropClick') {
          setIsOpen(false);
        }
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      }}
      fullWidth
      maxWidth="sm"
      closeAfterTransition
      container={document.body as any}
    >
      {dialogContent}
    </Dialog>,
    document.body as any,
  );
});

// Completely isolated version chip component with no Redux dependencies
const VersionChip = memo(({ sx, isMobile, isCollapse, authToken, isMobileQuery }: any) => {
  const versionData = useVersionData();

  const latestVersionLabel = useMemo(() => {
    const SHOW_PRE_RELEASE_VERSION = true;

    if (versionData.latestVersionSimple && versionData.preReleaseType)
      return isMobileQuery //
        ? `${versionData.latestVersionSimple} • ${versionData.preReleaseType}`
        : SHOW_PRE_RELEASE_VERSION
        ? `${versionData.latestVersionSimple} • ${versionData.preReleaseType} • ${versionData.preReleaseVersion}`
        : `${versionData.latestVersionSimple} • ${versionData.preReleaseType}`;
    if (versionData.latestVersionSimple) return isMobileQuery ? versionData.latestVersionSimple : versionData.latestVersionSimple;
    return isMobileQuery ? versionData.latestVersion : versionData.latestVersion;
  }, [versionData.latestVersionSimple, versionData.preReleaseType, versionData.latestVersion, isMobileQuery]);

  const handleChipClick = useCallback(
    (e: React.MouseEvent) => {
      if (authToken) setGlobalDialogState(true);
      e.stopPropagation();
      e.preventDefault();
    },
    [authToken],
  );

  return (
    <Tooltip title={authToken ? 'Display version history' : ''} enterDelay={500}>
      <Chip
        label={latestVersionLabel}
        color="error"
        onClick={handleChipClick}
        sx={{
          ...versionChipStylings,
          ...(isMobile ? versionChipStylings_mobile : isCollapse ? versionChipStylings_collapsed : versionChipStylings_expanded),
          ...sx,
          cursor: 'pointer',
          backgroundColor: (theme) => theme.palette.error.light,
          color: (theme) => theme.palette.error.dark,
          textTransform: 'capitalize',
          '&.MuiChip-root:hover': { filter: 'brightness(1.05)' },
          '&.MuiButtonBase-root:hover': authToken ? { backgroundColor: undefined, color: '#fff' } : { backgroundColor: (theme) => theme.palette.error.light, color: (theme) => theme.palette.error.dark },
        }}
      />
    </Tooltip>
  );
});

// Main Logo component with integrated functionality
const Logo = memo(({ align = 'center' }: LogoProps) => {
  const customizer = useSelector((state: AppState) => state.customizer);
  const hasAuthToken = useSelector((state: AppState) => state.authReducer?.authToken);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobileQuery = useMediaQuery(theme.breakpoints.down('lg'));

  const LinkStyled = styled(Link)(() => ({
    height: customizer.TopbarHeight,
    width: customizer.isCollapse ? '40px' : '180px',
    overflow: 'hidden',
    display: 'block',
  }));

  // Determine logo configuration
  const isDarkMode = customizer.activeMode === 'dark';
  const isExpanded = !customizer.isCollapse;
  // The lockup's viewBox hugs the ink (119.93 : 16.32, mark as tall as the text), so
  // 136px wide is an 18.5px mark next to a ~25px wordmark; the collapsed state shows
  // the mark alone in a 36px box.
  const logoConfig = {
    src: isDarkMode ? logoDark : logoLight,
    alt: isDarkMode ? 'Logo Dark' : 'Logo Light',
    width: isExpanded ? 136 : 36,
    height: isExpanded ? 'auto' : 36,
    style: {
      width: isExpanded ? '136px' : '36px',
      maxWidth: '180px',
      height: isExpanded ? 'auto' : '36px',
      objectFit: 'contain' as const,
      transition: 'all 1s ease',
    },
  };

  const containerStyle = {
    position: 'relative' as const,
    display: 'flex',
    flexDirection: isMobile ? ('column' as const) : ('row' as const),
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    ...(isExpanded && isDarkMode && { width: 'min-content' }),
  };

  const LogoSm = isDarkMode ? LogoDarkSm : LogoLightSm;
  const logoElement = isExpanded ? (
    <img src={logoConfig.src} alt={logoConfig.alt} width={logoConfig.width} height={logoConfig.height} style={logoConfig.style} />
  ) : (
    <LogoSm width={logoConfig.width} height={logoConfig.height} style={logoConfig.style} />
  );

  return (
    <>
      <LinkStyled
        to={hasAuthToken ? '/' : '/auth/login'}
        sx={{
          pt: 1,
          display: 'flex',
          justifyContent: align,
          alignItems: 'center',
          width: '100%',
          minWidth: 'min-content',
          cursor: hasAuthToken ? 'pointer' : 'default',
        }}
      >
        <Box sx={containerStyle}>
          <VersionChip isMobile={isMobile} isCollapse={customizer.isCollapse} authToken={hasAuthToken} isMobileQuery={isMobileQuery} />
          {logoElement}
        </Box>
      </LinkStyled>
      <VersionHistoryDialog />
    </>
  );
});

export default Logo;

const versionChipStylings = {
  position: 'absolute',
  right: '0px',
  paddingX: '4px',
  height: '12px',
  lineHeight: '8px',
  '& .MuiChip-label': {
    padding: '2px',
    fontSize: '8px',
    height: '12px',
  },
};
const versionChipStylings_expanded = {
  bottom: '4px',
};
const versionChipStylings_collapsed = {
  bottom: '-1px',
};
const versionChipStylings_mobile = {
  position: 'static',
  marginBottom: '4px',
  alignSelf: 'center',
};
